/**
 * Date-range filter bar — five preset chips (All time / 7d / 30d / 90d /
 * Custom) plus start/end pickers for the custom range. Ported from
 * aieweb/components/admin/signups-content.tsx (DateRangeBar). Scopes the table
 * + chart; the KPI cards stay lifetime regardless.
 *
 * Custom end date is INCLUSIVE. `customStart` > `customEnd` surfaces the inline
 * error (passed down from the parent) and sends no request.
 */
import type { DateRange, DateRangePreset } from "./signupsAggregation";

export const DATE_RANGE_LABELS: Record<DateRangePreset, string> = {
  all: "All time",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  custom: "Custom",
};

export interface DateRangeBarProps {
  value: DateRange;
  onChange: (next: DateRange) => void;
  error: string | null;
}

export function DateRangeBar({ value, onChange, error }: DateRangeBarProps) {
  const presets: DateRangePreset[] = ["all", "7d", "30d", "90d", "custom"];
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {presets.map((p) => {
        const active = value.preset === p;
        return (
          <button
            key={p}
            type="button"
            aria-pressed={active}
            onClick={() => onChange({ preset: p })}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              active
                ? "bg-primary text-primary-foreground border-accent-text"
                : "bg-card text-text-secondary border-border hover:bg-muted"
            }`}
          >
            {DATE_RANGE_LABELS[p]}
          </button>
        );
      })}
      {value.preset === "custom" && (
        <span className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-muted-foreground">
            Start
            <input
              type="date"
              value={value.customStart ?? ""}
              onChange={(e) => onChange({ ...value, customStart: e.target.value })}
              className="ml-2 px-2 py-1 text-sm border border-border rounded bg-card text-foreground"
            />
          </label>
          <label className="text-sm text-muted-foreground">
            End <span className="text-xs">(end date inclusive)</span>
            <input
              type="date"
              value={value.customEnd ?? ""}
              onChange={(e) => onChange({ ...value, customEnd: e.target.value })}
              className="ml-2 px-2 py-1 text-sm border border-border rounded bg-card text-foreground"
            />
          </label>
        </span>
      )}
      {error && (
        <span role="alert" className="text-sm text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}
